/**
 * Whether a hardware scanner is armed, and at which station, remembered by the machine it is in.
 *
 * Scan mode began as a piece of one screen's state, which meant it lasted exactly as long as that
 * screen was mounted. A gate computer is shared: somebody looks up a registration, opens results,
 * closes the lid, reloads because the wifi dropped. Every one of those turned the scanner off
 * silently — no strip, no sound, and scans that did nothing at all until a queue stopped moving and
 * somebody worked out why.
 *
 * **One scanner, so one armed gate.** A single key rather than one per event: the device has one
 * scanner cable in it, and arming a second event's gate on the same machine can only mean the first
 * one is over. Storing several would let a laptop believe it is at two gates at once, and the scan
 * that arrives has nothing on it to say which one it meant.
 *
 * The event's name is kept beside the id purely so the rail in the shell can say *which* station is
 * armed without loading the event to find out — the shell shows it on screens that have no event.
 *
 * **The station, because there is more than one now.** The gate scans people in; the payout table
 * scans them up to find what they are owed. Both are one cable in one laptop and the same "one
 * armed station" rule applies — but the rail in the shell has to say which screen the scans are
 * landing on, and the gate must not believe it is armed because the treasurer armed the till. A
 * value written before this existed has no station and is read as the gate, which is what it was.
 */

const KEY = "pw.scan";

/** Which screen a scan belongs to. */
export type ScanStation = "gate" | "payout";

/** What each station is called on the rail, in the words the screen itself uses. */
export const STATION_NAMES: Record<ScanStation, string> = {
  gate: "check-in",
  payout: "payouts",
};

export interface ArmedScanner {
  eventId: string;
  eventName: string;
  station: ScanStation;
}

/** The gate this machine is armed for, or null. Never throws — a locked-down profile just forgets. */
export function armedScanner(): ArmedScanner | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { eventId, eventName, station } = parsed as Partial<ArmedScanner>;
    if (typeof eventId !== "string" || !eventId) return null;
    return {
      eventId,
      eventName: typeof eventName === "string" ? eventName : "",
      station: station === "payout" ? "payout" : "gate",
    };
  } catch {
    // Something else wrote here, or a half-written value survived a crash. Not armed.
    return null;
  }
}

export function isArmed(eventId: string, station: ScanStation = "gate"): boolean {
  const armed = armedScanner();
  return armed?.eventId === eventId && armed.station === station;
}

/** Where the scans are landing, if anywhere — `/events/{eventId}/{path}`. */
export function stationPath(armed: ArmedScanner): string {
  return `/events/${armed.eventId}/${armed.station === "payout" ? "payouts" : "check-in"}`;
}

/** Arm this station — which disarms any other, there being one scanner — or disarm it. */
export function setArmed(gate: ArmedScanner | null): void {
  try {
    if (gate) localStorage.setItem(KEY, JSON.stringify(gate));
    else localStorage.removeItem(KEY);
  } catch {
    /* Private browsing or a kiosk profile: the gate still works, it just forgets. */
  }
}
